// Per-block loading affordances for the dashboard / reports. EVERY independent block (each card,
// each tile) shows its own indicator:
//  - first load: BlockPlaceholder — a skeleton of the block's size with its own spinner;
//  - refetch: Busy — the old content stays visible, dimmed, with the block's own spinner
//    (a centred "Loading…" pill on cards, a small corner spinner on compact tiles).

import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

function Spinner({ compact }: { compact?: boolean }) {
  return compact ? (
    <span role="status" aria-label="Loading" className="pointer-events-none absolute top-2 right-2 z-10">
      <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
    </span>
  ) : (
    <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center">
      <span
        role="status"
        className="flex items-center gap-1.5 rounded-full border border-border bg-popover px-3 py-1 text-xs text-muted-foreground shadow-sm"
      >
        <Loader2 className="size-3.5 animate-spin" /> Loading…
      </span>
    </div>
  );
}

/** Wraps ONE block; while busy its content is dimmed and it shows its own spinner. */
export function Busy({
  busy,
  compact,
  fill = false,
  children,
  className,
}: {
  busy: boolean;
  compact?: boolean;
  /** The block fills the available height (a list whose table scrolls inside it). */
  fill?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("relative min-w-0", className)} aria-busy={busy}>
      <div
        className={cn(
          "h-full min-w-0 transition-opacity duration-200",
          fill && "flex min-h-0 flex-col",
          busy && "pointer-events-none opacity-50"
        )}
      >
        {children}
      </div>
      {busy && <Spinner compact={compact} />}
    </div>
  );
}

/** First-load stand-in for ONE block: a skeleton with the block's own spinner. */
export function BlockPlaceholder({ className, compact }: { className?: string; compact?: boolean }) {
  return (
    <div className={cn("relative", className)} aria-busy>
      <Skeleton className="h-full w-full rounded-xl" />
      {compact ? (
        <Spinner compact />
      ) : (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span role="status" className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </span>
        </div>
      )}
    </div>
  );
}
